import type { BreadcrumbItem } from '@/components/ui/PanelBreadcrumbs'
import type { PanelContent } from '@/components/ui/PanelContent'
import { getPanelContentTitle } from '@/components/ui/PanelContent'
import { useCallback, useEffect, useMemo, useState } from 'react'

export interface PanelState {
  history: PanelContent[]
  breadcrumbs: BreadcrumbItem[]
  currentIndex: number
}

interface UsePanelOptions {
  initialContent?: PanelContent
  onNavigate?: (content: PanelContent, breadcrumbs: BreadcrumbItem[]) => void
  maxHistory?: number
  enableBrowserHistory?: boolean
  historyPrefix?: string
}

export function usePanel(options: UsePanelOptions = {}) {
  const {
    initialContent,
    onNavigate,
    maxHistory = 10,
    enableBrowserHistory = false,
    historyPrefix = 'panel',
  } = options

  const [panelState, setPanelState] = useState<PanelState>(() => {
    if (initialContent) {
      const title = getPanelContentTitle(initialContent)
      return {
        history: [initialContent],
        breadcrumbs: [{ label: title, id: '0', clickable: false }],
        currentIndex: 0,
      }
    }

    return {
      history: [],
      breadcrumbs: [],
      currentIndex: -1,
    }
  })

  const [prevIndex, setPrevIndex] = useState<number>(-1)

  // Current content
  const currentContent = useMemo(() => {
    if (panelState.currentIndex >= 0 && panelState.history[panelState.currentIndex]) {
      return panelState.history[panelState.currentIndex]
    }
    return null
  }, [panelState.currentIndex, panelState.history])

  // Push new content onto the stack
  const pushContent = useCallback(
    (content: PanelContent, customBreadcrumbs?: BreadcrumbItem[]) => {
      setPanelState((prevState) => {
        //const title = getPanelContentTitle(content)
        const newIndex = prevState.currentIndex + 1

        // Truncate history at current position and add new content
        const newHistory = [...prevState.history.slice(0, newIndex), content]

        // Limit history size
        const trimmedHistory = newHistory.slice(-maxHistory)
        const trimmedIndex = Math.min(newIndex, maxHistory - 1)

        // Generate breadcrumbs - use custom breadcrumbs if provided for the new content
        const newBreadcrumbs =
          customBreadcrumbs && newIndex === trimmedIndex
            ? customBreadcrumbs
            : trimmedHistory.map((item, index) => ({
                label: getPanelContentTitle(item),
                id: index.toString(),
                clickable: index < trimmedIndex,
              }))

        const newState = {
          history: trimmedHistory,
          breadcrumbs: newBreadcrumbs,
          currentIndex: trimmedIndex,
        }

        return newState
      })

      // Notify parent of navigation after state update
      if (onNavigate) {
        // Use setTimeout to ensure this runs after the current render cycle
        setTimeout(() => {
          const newIndex = panelState.currentIndex + 1
          const newHistory = [...panelState.history.slice(0, newIndex), content]
          const trimmedHistory = newHistory.slice(-maxHistory)
          const trimmedIndex = Math.min(newIndex, maxHistory - 1)
          const newBreadcrumbs =
            customBreadcrumbs && newIndex === trimmedIndex
              ? customBreadcrumbs
              : trimmedHistory.map((item, index) => ({
                  label: getPanelContentTitle(item),
                  id: index.toString(),
                  clickable: index < trimmedIndex,
                }))
          onNavigate(content, newBreadcrumbs)
        }, 0)
      }
    },
    [maxHistory, onNavigate, panelState]
  )

  // Navigate to a specific index in history
  const navigateToIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= panelState.history.length) {
        return
      }

      setPanelState((prevState) => {
        // Truncate history at the selected index
        const newHistory = prevState.history.slice(0, index + 1)
        const newBreadcrumbs = newHistory.map((item, i) => ({
          label: getPanelContentTitle(item),
          id: i.toString(),
          clickable: i < index,
        }))

        const newState = {
          history: newHistory,
          breadcrumbs: newBreadcrumbs,
          currentIndex: index,
        }

        return newState
      })

      // Notify parent of navigation after state update
      if (onNavigate && panelState.history[index]) {
        setTimeout(() => {
          const newHistory = panelState.history.slice(0, index + 1)
          const newBreadcrumbs = newHistory.map((item, i) => ({
            label: getPanelContentTitle(item),
            id: i.toString(),
            clickable: i < index,
          }))
          onNavigate(newHistory[index], newBreadcrumbs)
        }, 0)
      }
    },
    [panelState.history.length, onNavigate, panelState.history]
  )

  // Go back one step
  const goBack = useCallback(() => {
    if (panelState.currentIndex > 0) {
      navigateToIndex(panelState.currentIndex - 1)
    }
  }, [panelState.currentIndex, navigateToIndex])

  // Check if can go back
  const canGoBack = useMemo(() => {
    return panelState.currentIndex > 0
  }, [panelState.currentIndex])

  // Replace current content
  const replaceContent = useCallback(
    (content: PanelContent) => {
      if (panelState.currentIndex < 0) {
        pushContent(content)
        return
      }

      setPanelState((prevState) => {
        //const title = getPanelContentTitle(content)
        const newHistory = [...prevState.history]
        newHistory[prevState.currentIndex] = content

        const newBreadcrumbs = newHistory.map((item, index) => ({
          label: getPanelContentTitle(item),
          id: index.toString(),
          clickable: index < prevState.currentIndex,
        }))

        const newState = {
          history: newHistory,
          breadcrumbs: newBreadcrumbs,
          currentIndex: prevState.currentIndex,
        }

        return newState
      })

      // Notify parent of navigation after state update
      if (onNavigate) {
        setTimeout(() => {
          const newHistory = [...panelState.history]
          newHistory[panelState.currentIndex] = content
          const newBreadcrumbs = newHistory.map((item, index) => ({
            label: getPanelContentTitle(item),
            id: index.toString(),
            clickable: index < panelState.currentIndex,
          }))
          onNavigate(content, newBreadcrumbs)
        }, 0)
      }
    },
    [panelState.currentIndex, pushContent, onNavigate, panelState.history]
  )

  // Clear all history
  const clearHistory = useCallback(() => {
    setPanelState({
      history: [],
      breadcrumbs: [],
      currentIndex: -1,
    })
    setPrevIndex(-1)
  }, [])

  // Set initial content if not set
  const setInitialContent = useCallback(
    (content: PanelContent) => {
      if (panelState.history.length === 0) {
        pushContent(content)
      }
    },
    [panelState.history.length, pushContent]
  )

  // Update browser history when panel state changes
  useEffect(() => {
    if (!enableBrowserHistory) return

    const currentIndex = panelState.currentIndex

    // Skip if index hasn't changed
    if (currentIndex === prevIndex) return

    // Index changed - update browser history
    if (currentIndex >= 0) {
      // For the first panel item, push a "base" state first
      if (prevIndex === -1) {
        const baseState = { panelIndex: -1, prefix: historyPrefix }
        const baseUrl = window.location.pathname + window.location.search
        window.history.pushState(baseState, '', baseUrl)
      }

      const state = {
        panelIndex: currentIndex,
        prefix: historyPrefix,
      }
      const url = `${window.location.pathname}${window.location.search}#${historyPrefix}-${currentIndex}`
      window.history.pushState(state, '', url)
    }

    setPrevIndex(currentIndex)
  }, [enableBrowserHistory, historyPrefix, panelState.currentIndex, prevIndex])

  // Browser history integration - handle back/forward navigation
  useEffect(() => {
    if (!enableBrowserHistory) return

    const handlePopState = (event: PopStateEvent) => {
      const state = event.state
      if (state && state.panelIndex !== undefined && state.prefix === historyPrefix) {
        // Navigate to the specific index from browser history
        if (state.panelIndex >= 0 && state.panelIndex < panelState.history.length) {
          navigateToIndex(state.panelIndex)
        } else if (state.panelIndex === -1) {
          // Close panel if going back to base state
          clearHistory()
        }
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [
    enableBrowserHistory,
    historyPrefix,
    panelState.history.length,
    navigateToIndex,
    clearHistory,
  ])

  return {
    // State
    currentContent,
    breadcrumbs: panelState.breadcrumbs,
    canGoBack,

    // Actions
    pushContent,
    replaceContent,
    navigateToIndex,
    goBack,
    clearHistory,
    setInitialContent,

    // Full state for debugging
    panelState,
  }
}
